import { useMemo, useState } from 'react';
import { usePostcodeReport } from '@/hooks/usePostcodeReport';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { MapPin, TrendingUp, DollarSign, Target, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface PostcodeReportProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  compareEnabled?: boolean;
}

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))', 'hsl(var(--muted-foreground))'];

export function PostcodeReport({ startDate, endDate, timezone: propTimezone }: PostcodeReportProps) {
  const { timezone: contextTimezone } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;
  const { formatMoney } = useCompanyLocale();

  const { data: postcodeData = [], isLoading } = usePostcodeReport(startDate, endDate, timezone);
  const [minJobs, setMinJobs] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({ 
    key: null, 
    direction: null 
  });

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key: null, direction: null };
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3 w-3 ml-1 inline" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="h-3 w-3 ml-1 inline" />;
    return <ArrowDown className="h-3 w-3 ml-1 inline" />;
  };

  // Filter and sort by minimum jobs threshold
  const filteredData = useMemo(() => {
    let filtered = postcodeData.filter((p) => p.totalJobs >= minJobs);
    
    if (!sortConfig.key || !sortConfig.direction) return filtered;
    
    return [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof typeof a];
      const bValue = b[sortConfig.key as keyof typeof b];
      
      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [postcodeData, minJobs, sortConfig]);

  // Get top 10 for chart
  const top10Data = useMemo(() => {
    return filteredData.slice(0, 10);
  }, [filteredData]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[400px] w-full" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  const totalRevenue = filteredData.reduce((sum, p) => sum + p.totalRevenue, 0);
  const totalJobs = filteredData.reduce((sum, p) => sum + p.totalJobs, 0);
  const totalLeads = filteredData.reduce((sum, p) => sum + p.totalLeads, 0);
  const overallConversion = totalLeads > 0 ? (totalJobs / totalLeads) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Postcodes</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredData.length}</div>
            <p className="text-xs text-muted-foreground">
              With {minJobs}+ jobs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMoney(totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              From all postcodes
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalJobs}</div>
            <p className="text-xs text-muted-foreground">
              Completed jobs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallConversion.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {totalLeads} total leads
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Chart - Top 10 Postcodes */}
      <Card>
        <CardHeader>
          <CardTitle>Top 10 Postcodes by Revenue</CardTitle>
          <CardDescription>Highest revenue generating locations</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={top10Data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="postcode" />
              <YAxis />
              <Tooltip 
                formatter={(value: number) => formatMoney(value)}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))'
                }}
              />
              <Bar dataKey="totalRevenue" fill="hsl(var(--primary))">
                {top10Data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Postcode Performance Details</CardTitle>
              <CardDescription>All postcodes with activity in the selected period</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="minJobs" className="text-sm">Min Jobs:</Label>
              <Input
                id="minJobs"
                type="number"
                min="1"
                value={minJobs}
                onChange={(e) => setMinJobs(parseInt(e.target.value) || 1)}
                className="w-20"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Postcode</TableHead>
                  <TableHead>Metro</TableHead>
                  <TableHead>Suburb</TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('totalJobs')}
                  >
                    Jobs {getSortIcon('totalJobs')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('totalRevenue')}
                  >
                    Revenue {getSortIcon('totalRevenue')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('averageTicket')}
                  >
                    Avg Ticket {getSortIcon('averageTicket')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('totalLeads')}
                  >
                    Leads {getSortIcon('totalLeads')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('conversionRate')}
                  >
                    Conversion {getSortIcon('conversionRate')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No postcode data available for the selected period
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((postcode) => (
                    <TableRow key={postcode.postcode}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {postcode.postcode}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {postcode.metro_name || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {postcode.suburb || '-'}
                      </TableCell>
                      <TableCell className="text-right">{postcode.totalJobs}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatMoney(postcode.totalRevenue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatMoney(postcode.averageTicket)}
                      </TableCell>
                      <TableCell className="text-right">{postcode.totalLeads}</TableCell>
                      <TableCell className="text-right">
                        <span className={postcode.conversionRate >= 50 ? 'text-success font-medium' : ''}>
                          {postcode.conversionRate.toFixed(1)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
